/**
 * Runtime gates and layout helpers for draft PR22 team fields.
 *
 * TypeScript types do not exist at runtime. A stale/tampered proxy response, a
 * foreign project/source/user, unknown keys or an over-bound catalogue must never
 * be rendered as trusted team data. One invalid response fails the relevant read
 * closed. Manual values are never merged into the R5 verified projection.
 */

import { ApiError } from "../api/client";
import type {
  OpportunityFieldDefinition,
  OpportunityFieldOption,
  OpportunityFieldSettings,
  OpportunityFieldValueDocument,
  OpportunityFieldValuePage,
  OpportunityFieldValue,
  OpportunityManualValueWriteCapability,
  OpportunityLayout,
  OpportunityPersonalView,
  OpportunityPersonalViewEnvelope,
} from "../api/client";

export const BUILTIN_COLUMN_KEYS = [
  "builtin:organization",
  "builtin:status",
  "builtin:opportunity",
  "builtin:organizationType",
  "builtin:region",
  "builtin:fit",
  "builtin:confidence",
  "builtin:reasons",
  "builtin:sources",
] as const;

export const DEFAULT_HIDDEN_COLUMNS = [
  "builtin:organizationType",
  "builtin:region",
  "builtin:sources",
] as const;

export const DEFAULT_LAYOUT: OpportunityLayout = {
  order: [...BUILTIN_COLUMN_KEYS],
  hidden: [...DEFAULT_HIDDEN_COLUMNS],
};

const BUILTIN_LABELS: Record<string, string> = {
  "builtin:organization": "Organizacija",
  "builtin:status": "Status",
  "builtin:opportunity": "Tip prilike",
  "builtin:organizationType": "Tip organizacije",
  "builtin:region": "Regija",
  "builtin:fit": "Fit",
  "builtin:confidence": "Pouzdanost",
  "builtin:reasons": "Razlozi",
  "builtin:sources": "Broj izvora",
};

const MAX_DEFINITIONS = 20;
const MAX_OPTIONS = 20;
const MAX_VALUES = 20;
const MAX_LABEL = 80;
const MAX_TEXT = 2000;
const MAX_NUMBER = 1_000_000_000_000_000;
const MAX_VERSION = 1_000_000;
const MAX_COLUMNS = 29;
const FIELD_ID = /^fld_[0-9a-f]{32}$/;
const OPTION_ID = /^opt_[0-9a-f]{32}$/;
const SETTINGS_ID = /^ofs_[0-9a-f]{32}$/;
const VALUE_ID = /^ofv_[0-9a-f]{32}$/;
const VIEW_ID = /^opv_[0-9a-f]{32}$/;
const PROJECT_ID = /^prj_[0-9a-z_]+$/;
const USER_ID = /^usr_[0-9a-z_]+$/;
const JOB_ID = /^job_[0-9a-z_]+$/;
const ORG_ID = /^org_[0-9a-z_]+$/;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const COLUMN = /^(builtin:[A-Za-z][A-Za-z]*|custom:fld_[0-9a-f]{32})$/;
const TYPES = new Set(["text", "number", "date", "select"]);

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwn(value: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => hasOwn(value, key));
}

function isText(value: unknown, maximum: number, minimum = 1): value is string {
  return typeof value === "string" && value.length >= minimum && value.length <= maximum;
}

function isLabel(value: unknown): value is string {
  return isText(value, MAX_LABEL) && value.trim().length > 0;
}

function isDateTime(value: unknown): value is string {
  return isText(value, 40) && DATE_TIME.test(value) && !Number.isNaN(Date.parse(value));
}

function isVersion(value: unknown, minimum = 0): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= minimum &&
    value <= MAX_VERSION
  );
}

function isManualValue(value: unknown): value is string | number {
  if (typeof value === "string") return value.length <= MAX_TEXT;
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    Math.abs(value) <= MAX_NUMBER
  );
}

/** Strict shape check for a layout; every builtin column is required even when
 * `expected` is omitted, and `expected` additionally requires the exact columns. */
export function parseOpportunityLayout(
  value: unknown,
  expected?: readonly string[],
): OpportunityLayout | null {
  if (!isRecord(value) || !hasExactKeys(value, ["order", "hidden"])) return null;
  const order = value.order;
  const hidden = value.hidden;
  if (!Array.isArray(order) || !Array.isArray(hidden)) return null;
  if (order.length > MAX_COLUMNS || hidden.length > MAX_COLUMNS) return null;
  if (!order.every((key) => typeof key === "string" && COLUMN.test(key))) return null;
  if (!hidden.every((key) => typeof key === "string" && COLUMN.test(key))) return null;
  if (order.some((key) => key.startsWith("builtin:")
    && !(BUILTIN_COLUMN_KEYS as readonly string[]).includes(key))) return null;
  if (hidden.some((key) => key.startsWith("builtin:")
    && !(BUILTIN_COLUMN_KEYS as readonly string[]).includes(key))) return null;
  if (new Set(order).size !== order.length) return null;
  if (new Set(hidden).size !== hidden.length) return null;
  // The public layout is closed over all nine builtin columns. A partial builtin
  // order (including order: [] or only builtin:fit) must fail closed even for a
  // personal view where no expected catalogue is supplied. Bounded custom keys,
  // including known-but-now-archived ids, remain accepted so the caller can
  // reconcile them explicitly instead of discarding the whole preference.
  if (order.length < BUILTIN_COLUMN_KEYS.length) return null;
  if (!BUILTIN_COLUMN_KEYS.every((key) => order.includes(key))) return null;
  if (hidden.some((key) => !order.includes(key))) return null;
  if (hidden.includes("builtin:organization")) return null;
  if (expected !== undefined) {
    if (order.length !== expected.length) return null;
    if (!expected.every((key) => order.includes(key))) return null;
  }
  return { order: [...order], hidden: [...hidden] };
}

export function activeColumnKeys(definitions: OpportunityFieldDefinition[]): string[] {
  return [
    ...BUILTIN_COLUMN_KEYS,
    ...definitions
      .filter((definition) => !definition.archived)
      .map((definition) => `custom:${definition.id}`),
  ];
}

function parseOption(value: unknown): OpportunityFieldOption | null {
  if (!isRecord(value) || !hasExactKeys(value, ["id", "label", "archived"])) return null;
  if (typeof value.id !== "string" || !OPTION_ID.test(value.id)) return null;
  if (!isLabel(value.label) || typeof value.archived !== "boolean") return null;
  return { id: value.id, label: value.label, archived: value.archived };
}

function parseDefinition(value: unknown): OpportunityFieldDefinition | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["id", "label", "type", "options", "archived"])
  ) {
    return null;
  }
  if (typeof value.id !== "string" || !FIELD_ID.test(value.id)) return null;
  if (!isLabel(value.label) || typeof value.archived !== "boolean") return null;
  if (typeof value.type !== "string" || !TYPES.has(value.type)) return null;
  if (!Array.isArray(value.options) || value.options.length > MAX_OPTIONS) return null;
  const options: OpportunityFieldOption[] = [];
  for (const raw of value.options) {
    const option = parseOption(raw);
    if (option === null) return null;
    options.push(option);
  }
  if (value.type === "select") {
    if (options.length < 1) return null;
  } else if (options.length !== 0) {
    return null;
  }
  if (new Set(options.map((option) => option.id)).size !== options.length) return null;
  return {
    id: value.id,
    label: value.label,
    type: value.type as OpportunityFieldDefinition["type"],
    options,
    archived: value.archived,
  };
}

/** Closed settings projection. Virtual default (version 0/id null) is supported. */
export function parseOpportunityFieldSettings(
  value: unknown,
  expectedProjectId: string,
): OpportunityFieldSettings | null {
  const keys = [
    "schema_version",
    "id",
    "project_id",
    "definitions",
    "shared_layout",
    "created_by",
    "created_at",
    "updated_by",
    "updated_at",
    "version",
  ] as const;
  if (!isRecord(value) || !hasExactKeys(value, keys)) return null;
  if (value.schema_version !== "r5.opportunity_field_settings.v1") return null;
  if (value.project_id !== expectedProjectId || !PROJECT_ID.test(expectedProjectId)) return null;
  if (!Array.isArray(value.definitions) || value.definitions.length > MAX_DEFINITIONS) return null;
  const definitions: OpportunityFieldDefinition[] = [];
  for (const raw of value.definitions) {
    const definition = parseDefinition(raw);
    if (definition === null) return null;
    definitions.push(definition);
  }
  if (new Set(definitions.map((definition) => definition.id)).size !== definitions.length) {
    return null;
  }
  const optionIds = definitions.flatMap((definition) =>
    definition.options.map((option) => option.id),
  );
  if (new Set(optionIds).size !== optionIds.length) return null;
  if (!isVersion(value.version)) return null;
  const id = value.id;
  if (id !== null && (typeof id !== "string" || !SETTINGS_ID.test(id))) return null;
  if (value.version === 0) {
    if (
      id !== null ||
      definitions.length !== 0 ||
      value.created_by !== null ||
      value.created_at !== null ||
      value.updated_by !== null ||
      value.updated_at !== null
    ) {
      return null;
    }
  } else {
    if (id === null) return null;
    if (typeof value.created_by !== "string" || !USER_ID.test(value.created_by)) return null;
    if (!isDateTime(value.created_at)) return null;
    if (typeof value.updated_by !== "string" || !USER_ID.test(value.updated_by)) return null;
    if (!isDateTime(value.updated_at)) return null;
  }
  const active = activeColumnKeys(definitions);
  const sharedLayout = parseOpportunityLayout(value.shared_layout, active);
  if (sharedLayout === null) return null;
  return {
    schema_version: "r5.opportunity_field_settings.v1",
    id,
    project_id: value.project_id,
    definitions,
    shared_layout: sharedLayout,
    created_by: value.created_by as string | null,
    created_at: value.created_at as string | null,
    updated_by: value.updated_by as string | null,
    updated_at: value.updated_at as string | null,
    version: value.version,
  };
}

function parseValueItem(value: unknown): OpportunityFieldValue | null {
  if (!isRecord(value) || !hasExactKeys(value, ["field_id", "value"])) return null;
  if (typeof value.field_id !== "string" || !FIELD_ID.test(value.field_id)) return null;
  if (!isManualValue(value.value)) return null;
  return { field_id: value.field_id, value: value.value };
}

export function parseOpportunityFieldValue(
  value: unknown,
  expectedProjectId: string,
  expectedSourceJobId?: string,
  expectedDecisionsHash?: string,
): OpportunityFieldValueDocument | null {
  const keys = [
    "schema_version",
    "id",
    "project_id",
    "source",
    "organization_id",
    "values",
    "created_by",
    "created_at",
    "updated_by",
    "updated_at",
    "version",
  ] as const;
  if (!isRecord(value) || !hasExactKeys(value, keys)) return null;
  if (value.schema_version !== "r5.opportunity_field_value.v1") return null;
  if (value.project_id !== expectedProjectId) return null;
  if (typeof value.id !== "string" || !VALUE_ID.test(value.id)) return null;
  if (!isRecord(value.source) || !hasExactKeys(value.source, ["job_id", "decisions_hash"])) {
    return null;
  }
  if (
    typeof value.source.job_id !== "string" ||
    !JOB_ID.test(value.source.job_id) ||
    value.source.job_id.length > 64
  ) {
    return null;
  }
  if (
    typeof value.source.decisions_hash !== "string" ||
    !DIGEST.test(value.source.decisions_hash)
  ) {
    return null;
  }
  if (expectedSourceJobId !== undefined && value.source.job_id !== expectedSourceJobId) return null;
  if (
    expectedDecisionsHash !== undefined &&
    value.source.decisions_hash !== expectedDecisionsHash
  ) {
    return null;
  }
  if (
    typeof value.organization_id !== "string" ||
    !ORG_ID.test(value.organization_id) ||
    value.organization_id.length > 64
  ) {
    return null;
  }
  if (!Array.isArray(value.values) || value.values.length > MAX_VALUES) return null;
  const values: OpportunityFieldValue[] = [];
  for (const raw of value.values) {
    const item = parseValueItem(raw);
    if (item === null) return null;
    values.push(item);
  }
  if (new Set(values.map((item) => item.field_id)).size !== values.length) return null;
  if (typeof value.created_by !== "string" || !USER_ID.test(value.created_by)) return null;
  if (typeof value.updated_by !== "string" || !USER_ID.test(value.updated_by)) return null;
  if (!isDateTime(value.created_at) || !isDateTime(value.updated_at)) return null;
  if (!isVersion(value.version, 1)) return null;
  return {
    schema_version: "r5.opportunity_field_value.v1",
    id: value.id,
    project_id: value.project_id,
    source: {
      job_id: value.source.job_id,
      decisions_hash: value.source.decisions_hash,
    },
    organization_id: value.organization_id,
    values,
    created_by: value.created_by,
    created_at: value.created_at,
    updated_by: value.updated_by,
    updated_at: value.updated_at,
    version: value.version,
  };
}

function parseManualValueWriteCapability(
  value: unknown,
): OpportunityManualValueWriteCapability | null {
  if (!isRecord(value) || !hasExactKeys(value, ["supported", "reason"])) return null;
  if (typeof value.supported !== "boolean") return null;
  if (value.supported) {
    if (value.reason !== null) return null;
  } else if (value.reason !== "feature_requires_replica_set") {
    return null;
  }
  return {
    supported: value.supported,
    reason: value.reason as "feature_requires_replica_set" | null,
  };
}

export function parseOpportunityFieldValuePage(
  value: unknown,
  expectedProjectId: string,
  expectedSourceJobId: string,
  expectedDecisionsHash: string,
): OpportunityFieldValuePage | null {
  if (!isRecord(value)
    || !hasExactKeys(value, ["items", "manual_value_write"])) return null;
  if (!Array.isArray(value.items) || value.items.length > MAX_VALUES) return null;
  const items: OpportunityFieldValueDocument[] = [];
  for (const raw of value.items) {
    const item = parseOpportunityFieldValue(
      raw, expectedProjectId, expectedSourceJobId, expectedDecisionsHash,
    );
    if (item === null) return null;
    items.push(item);
  }
  if (new Set(items.map((item) => item.organization_id)).size !== items.length) return null;
  const capability = parseManualValueWriteCapability(value.manual_value_write);
  if (capability === null) return null;
  return { items, manual_value_write: capability };
}

export function parseOpportunityPersonalView(
  value: unknown,
  expectedProjectId: string,
  expectedUserId?: string,
): OpportunityPersonalView | null {
  const keys = [
    "schema_version",
    "id",
    "project_id",
    "user_id",
    "layout",
    "use_project_default",
    "version",
    "created_at",
    "updated_at",
  ] as const;
  if (!isRecord(value) || !hasExactKeys(value, keys)) return null;
  if (value.schema_version !== "r5.opportunity_personal_view.v1") return null;
  if (value.project_id !== expectedProjectId) return null;
  if (typeof value.id !== "string" || !VIEW_ID.test(value.id)) return null;
  if (typeof value.user_id !== "string" || !USER_ID.test(value.user_id)) return null;
  if (expectedUserId !== undefined && value.user_id !== expectedUserId) return null;
  if (typeof value.use_project_default !== "boolean") return null;
  const layout = value.layout === null ? null : parseOpportunityLayout(value.layout);
  if (value.layout !== null && layout === null) return null;
  if (value.use_project_default !== (layout === null)) return null;
  if (!isVersion(value.version, 1)) return null;
  if (!isDateTime(value.created_at) || !isDateTime(value.updated_at)) return null;
  return {
    schema_version: "r5.opportunity_personal_view.v1",
    id: value.id,
    project_id: value.project_id,
    user_id: value.user_id,
    layout,
    use_project_default: value.use_project_default,
    version: value.version,
    created_at: value.created_at,
    updated_at: value.updated_at,
  };
}

export function parseOpportunityPersonalViewEnvelope(
  value: unknown,
  expectedProjectId: string,
  expectedUserId?: string,
): OpportunityPersonalViewEnvelope | null {
  if (!isRecord(value) || !hasExactKeys(value, ["item"])) return null;
  if (value.item === null) return { item: null };
  const item = parseOpportunityPersonalView(value.item, expectedProjectId, expectedUserId);
  return item === null ? null : { item };
}

/**
 * Remove a column whose custom definition is now archived and append any newly
 * added active column. The stored preference is never silently mutated; the UI
 * reports what changed and the next explicit save writes the reconciled layout.
 */
export function reconcileOpportunityLayout(
  layout: OpportunityLayout,
  definitions: OpportunityFieldDefinition[],
): { layout: OpportunityLayout; removed: string[]; added: string[] } {
  const active = activeColumnKeys(definitions);
  const activeSet = new Set(active);
  const order: string[] = [];
  for (const key of layout.order) {
    if (activeSet.has(key) && !order.includes(key)) order.push(key);
  }
  const removed = layout.order.filter((key) => !activeSet.has(key));
  const added = active.filter((key) => !order.includes(key));
  order.push(...added);
  const hidden = layout.hidden.filter(
    (key) => order.includes(key) && key !== "builtin:organization",
  );
  return { layout: { order, hidden }, removed, added };
}

export function defaultSharedLayout(definitions: OpportunityFieldDefinition[]): OpportunityLayout {
  const order = activeColumnKeys(definitions);
  return { order, hidden: DEFAULT_HIDDEN_COLUMNS.filter((key) => order.includes(key)) };
}

export function columnLabel(
  key: string,
  definitions: OpportunityFieldDefinition[],
): string {
  if (key in BUILTIN_LABELS) return BUILTIN_LABELS[key];
  if (key.startsWith("custom:")) {
    const fieldId = key.slice("custom:".length);
    const definition = definitions.find((item) => item.id === fieldId);
    if (definition) return definition.archived ? `${definition.label} (arhivirano)` : definition.label;
  }
  return key;
}

export function isBuiltinColumn(key: string): boolean {
  return key.startsWith("builtin:") && key.slice("builtin:".length) in BUILTIN_KEYS;
}

const BUILTIN_KEYS: Record<string, true> = {
  organization: true,
  status: true,
  opportunity: true,
  organizationType: true,
  region: true,
  fit: true,
  confidence: true,
  reasons: true,
  sources: true,
};

export function fieldIdFromColumn(key: string): string | null {
  return key.startsWith("custom:") && FIELD_ID.test(key.slice("custom:".length))
    ? key.slice("custom:".length)
    : null;
}

const FIELD_ERROR_MESSAGES: Record<string, string> = {
  version_conflict:
    "Netko je promijenio timska polja ili raspored prije vas. Osvježite i ponovno učitajte prije spremanja.",
  field_limit_reached:
    "Dosegnut je limit od 20 definicija polja po projektu (uključujući arhivirana). Arhivirana polja se ne recikliraju.",
  option_limit_reached:
    "Dosegnut je limit od 20 opcija za ovo polje (uključujući arhivirane).",
  value_limit_reached:
    "Dosegnut je limit od 20 ručnih vrijednosti za ovaj zapis ili 1000 zapisa po projektu.",
  personal_view_limit_reached:
    "Dosegnut je limit od 100 osobnih prikaza po projektu.",
  invalid_layout:
    "Raspored stupaca nije valjan za trenutačni katalog polja. Osvježite i pokušajte ponovno.",
  invalid_value: "Vrijednost nije valjana za odabrani tip polja.",
  archived_field: "Polje je arhivirano i njegove se vrijednosti više ne mogu mijenjati.",
  archived_option: "Opcija je arhivirana i ne može se odabrati.",
  option_archived: "Opcija je već arhivirana.",
  unknown_option: "Odabrana opcija ne pripada ovom polju.",
  unknown_field: "Polje ne postoji u trenutačnom katalogu projekta.",
  invalid_source:
    "Izvorna analiza nije prošla provjeru; timske vrijednosti nisu spremljene.",
  source_not_succeeded:
    "Izvorni posao nije uspješno završio; vrijednosti se ne mogu spremiti.",
  source_decisions_mismatch:
    "Izvorna analiza ne odgovara točnom skupu odluka; vrijednosti nisu spremljene.",
  unknown_organization:
    "Organizacija ne pripada ovoj izvornoj analizi; vrijednosti nisu spremljene.",
  no_values: "Unesite barem jednu vrijednost.",
  outbox_backpressure:
    "Audit red je pun ili blokiran; promjena nije spremljena. Pokušajte kasnije.",
  feature_requires_replica_set:
    "Spremanje timskih vrijednosti nije dostupno na ovoj bazi jer nije MongoDB replica set. Postojeće vrijednosti, katalog i raspored ostaju dostupni za čitanje.",
  commit_outcome_unknown:
    "Ishod spremanja nije potvrđen; moguće je da je unos već spremljen. Osvježite prikaz i provjerite stanje prije ponovnog pokušaja.",
  forbidden: "Nemate pravo upravljati zajedničkim timskim poljima.",
  not_found: "Projekt, polje ili zapis nije dostupan.",
};

export function fieldErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    return FIELD_ERROR_MESSAGES[error.detail] ?? error.messageHr;
  }
  return "Timski podaci nisu dostupni ili nisu prošli provjeru. Prikaz je blokiran.";
}
